import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Container } from "@/components/Container";
import { Section } from "@/components/Section";
import { cn } from "@/lib/utils";
import { getHealthProduct } from "@/lib/cms/health-products.functions";
import { AddToCartButton, PriceLine, packLabel } from "@/components/health-products/ProductCard";

const productQuery = (slug: string) =>
  queryOptions({
    queryKey: ["health-products", "detail", slug],
    queryFn: () => getHealthProduct({ data: { slug } }),
    staleTime: 60_000,
  });

export const Route = createFileRoute("/health-products/$slug")({
  loader: async ({ context, params }) => {
    try {
      return await context.queryClient.ensureQueryData(productQuery(params.slug));
    } catch {
      return undefined;
    }
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Health Products | Poonam Kapur Healthy Kitchen" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://poonamkapur.com/health-products/${loaderData.slug}`;
    // Customer-payable price (GST-inclusive), matching checkout totals.
    const payable = loaderData.priceIncludesGst
      ? loaderData.pricePaise
      : Math.round(loaderData.pricePaise * (1 + loaderData.gstRatePercent / 100));
    const title = loaderData.seoTitle || `${loaderData.name} | Poonam Kapur Healthy Kitchen`;
    const desc = loaderData.seoDescription || loaderData.note || `${loaderData.name} from Poonam Kapur Healthy Kitchen.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: loaderData.imageUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: loaderData.imageUrl },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: loaderData.name,
            sku: loaderData.sku,
            image: [loaderData.imageUrl, ...loaderData.gallery].filter(Boolean),
            ...(loaderData.description || loaderData.note
              ? { description: loaderData.description || loaderData.note }
              : {}),
            ...(loaderData.brand ? { brand: { "@type": "Brand", name: loaderData.brand } } : {}),
            ...(loaderData.category ? { category: loaderData.category } : {}),
            url,
            offers: {
              "@type": "Offer",
              url,
              priceCurrency: "INR",
              price: (payable / 100).toFixed(2),
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/NewCondition",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://poonamkapur.com/" },
              { "@type": "ListItem", position: 2, name: "Health Products", item: "https://poonamkapur.com/health-products" },
              { "@type": "ListItem", position: 3, name: loaderData.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: HealthProductPage,
});

function HealthProductPage() {
  // Loader data is serialised with the page, so server and browser render the same state.
  const loaded = Route.useLoaderData();
  const p = loaded ?? null;
  const isError = loaded === undefined;
  const [active, setActive] = useState(0);

  const back = (
    <Link
      to="/health-products"
      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> All health products
    </Link>
  );

  if (isError || !p) {
    return (
      <SiteLayout>
        <Container className="pt-16 md:pt-24">
          {back}
          <h1 className="mt-8 font-display text-3xl">
            {isError ? "We couldn't load this product" : "This product isn't available"}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {isError ? "Please try again in a moment." : "It may be out of stock or no longer listed."}
          </p>
        </Container>
      </SiteLayout>
    );
  }

  const images = [p.imageUrl, ...p.gallery].filter(Boolean);
  const pack = packLabel(p);
  const details: Array<[string, string | null]> = [
    ["Ingredients", p.ingredients],
    ["Directions for use", p.directions],
    ["Warnings", p.warnings],
  ];

  return (
    <SiteLayout>
      <Container className="pt-12 md:pt-16">{back}</Container>
      <Section className="pt-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="aspect-square overflow-hidden rounded-2xl bg-muted">
              <img src={images[active]} alt={p.altText} className="h-full w-full object-cover" />
            </div>
            {images.length > 1 ? (
              <div className="mt-4 flex flex-wrap gap-3">
                {images.map((src, i) => (
                  <button
                    key={src + i}
                    onClick={() => setActive(i)}
                    aria-label={`Show image ${i + 1}`}
                    className={cn("h-20 w-20 overflow-hidden rounded-lg border-2", i === active ? "border-primary" : "border-transparent")}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-5">
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {[p.brand, p.category].filter(Boolean).join(" · ")}
            </span>
            <h1 className="font-display text-4xl leading-tight md:text-5xl">{p.name}</h1>
            {p.note ? <p className="text-lg text-muted-foreground">{p.note}</p> : null}
            <PriceLine p={p} />
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
              {pack ? (<><dt className="text-muted-foreground">Pack</dt><dd>{pack}</dd></>) : null}
              <dt className="text-muted-foreground">Availability</dt>
              <dd>{p.lowStock ? "In stock — only a few left" : "In stock"}</dd>
              {p.sku ? (<><dt className="text-muted-foreground">SKU</dt><dd>{p.sku}</dd></>) : null}
            </dl>
            <div className="flex flex-col gap-2">
              <AddToCartButton product={p} showTotal className="gap-2 rounded-full px-8" />
              <Link to="/health-products/cart" className="text-sm underline">View cart</Link>
            </div>
            {p.description ? <p className="whitespace-pre-wrap leading-relaxed text-foreground/85">{p.description}</p> : null}
            {details.filter(([, v]) => v).map(([label, v]) => (
              <div key={label}>
                <h2 className="font-display text-lg">{label}</h2>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/85">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}
